import React, { useEffect, useRef, useState } from "react";
import { runtimeConfig } from "@/lib/config";

const SCRIPT_ID = "aureon-turnstile-script";
let turnstileLoader;

function loadTurnstile() {
  if (window.turnstile) return Promise.resolve(window.turnstile);
  if (turnstileLoader) return turnstileLoader;
  turnstileLoader = new Promise((resolve, reject) => {
    const existing = document.getElementById(SCRIPT_ID);
    if (existing) {
      existing.addEventListener("load", () => resolve(window.turnstile), { once: true });
      existing.addEventListener("error", reject, { once: true });
      return;
    }
    const script = document.createElement("script");
    script.id = SCRIPT_ID;
    script.src = "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";
    script.async = true;
    script.defer = true;
    script.onload = () => window.turnstile ? resolve(window.turnstile) : reject(new Error("Turnstile did not initialize"));
    script.onerror = (error) => {
      script.remove();
      reject(error);
    };
    document.head.appendChild(script);
  }).catch((error) => {
    document.getElementById(SCRIPT_ID)?.remove();
    turnstileLoader = null;
    throw error;
  });
  return turnstileLoader;
}

export function TurnstileWidget({ onToken, resetKey }) {
  const containerRef = useRef(null);
  const [loadError, setLoadError] = useState(false);
  const [retry, setRetry] = useState(0);

  useEffect(() => {
    if (!runtimeConfig.turnstileSiteKey) return undefined;
    setLoadError(false);
    let mounted = true;
    let widgetId;
    loadTurnstile()
      .then((turnstile) => {
        if (!mounted || !containerRef.current) return;
        widgetId = turnstile.render(containerRef.current, {
          sitekey: runtimeConfig.turnstileSiteKey,
          theme: "dark",
          size: "flexible",
          action: "lead-form",
          callback: (token) => onToken(token),
          "expired-callback": () => onToken(""),
          "error-callback": () => onToken(""),
        });
      })
      .catch(() => {
        onToken("");
        setLoadError(true);
      });
    return () => {
      mounted = false;
      if (widgetId !== undefined && window.turnstile) window.turnstile.remove(widgetId);
    };
  }, [onToken, resetKey, retry]);

  if (!runtimeConfig.turnstileSiteKey) return null;
  return (
    <div>
      <div ref={containerRef} className="min-h-[65px]" data-testid="turnstile-widget" />
      {loadError && (
        <button type="button" onClick={() => setRetry((value) => value + 1)} className="text-xs text-red-200 underline">
          Security check failed to load. Try again.
        </button>
      )}
    </div>
  );
}
